import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal } from 'react-bootstrap';
import { Row } from 'react-table';
import { DraggableModalDialog } from './DraggableModalDialog';
import DataTable from '../DataTable/datatable.component';
import DateColumn from '../DataTable/datatable.datecolumn';
import NoFilter from '../DataTable/datatable.nofilter';
import { Quality, QualityValueSample, QualityColumnType } from '../Types/LotDetailQuality';

export type SampleTableProps = {
    quality: Quality,
    samples: QualityValueSample[],
    qualityColumns: QualityColumnType[]
};

function SampleTable({ quality, samples, qualityColumns }: SampleTableProps ) {

    const { t } = useTranslation();

    var tableData = samples;

    const tableColumns: any[] = [
        {
            Header: t('LabDetails.Samples.SampleId'),
            accessor: 'sampleID'
        },
        DateColumn({ header: t('LabDetails.Samples.SampleTime'), accessor: 'sampleTimeUtc', width: "15%" }),
        {
            Header: t('LabDetails.Samples.SamplePoint'),
            accessor: 'samplePoint'
        },
        {
            Header: t('LabDetails.Samples.Value'),
            accessor: 'value'
        },
    ];

    // Add quality based columns (using the list of quality columns to decide what to add)
    qualityColumns.forEach((qualityColumnType) => {
        switch (qualityColumnType) {

            case "LRL": // Release Limits don't make sense for individual results - show the minimum limit
            case "Min":
                tableColumns.push({
                    Header: t('LotDetails.Qualities.Min'),
                    Cell: (props: any) => quality.min
                });
                break;
            case "URL": // Release Limits don't make sense for individual results - show the maximum limit
            case "Max":
                tableColumns.push({
                    Header: t('LotDetails.Qualities.Max'),
                    Cell: (props: any) => quality.max
                });
                break;
            case "Target":
                tableColumns.push({
                    Header: t('LotDetails.Qualities.Target'),
                    Cell: (props: any) => quality.target
                });
                break;
        }
    });

    tableColumns.push({
        Header: t('LabDetails.Samples.IsOffspec'),
        accessor: 'isOffspec',
        Filter: NoFilter,
        Cell: (props: any) => {
            return (props.value === true ? "Yes" : "No")
        }
    });

    const getRowProps = (row: Row<QualityValueSample>) => {
        if (row.original.isOffspec)
            return { className: "offspec" };

        return {};
    };

    const initialState = {
        sortBy: [
            {
                id: 'sampleTimeUtc',
                desc: false
            },
            {
                id: 'sampleID',
                desc: false
            }
        ]
    };

    return (
        <DataTable
            id={`${quality}-lab-results-table`}
            tableColumns={tableColumns}
            tableData={tableData}
            initialState={initialState}
            getRowProps={getRowProps}
            showPaging={false} />
    );
}

export type LabDetailsDialogProps = {
    quality: Quality,
    samples: QualityValueSample[],
    qualityColumns: QualityColumnType[],
    showDialog: boolean,
    closeDialog: () => void
};

function LabDetailsDialog({ showDialog, closeDialog, quality, samples, qualityColumns }: LabDetailsDialogProps) {

    const { t } = useTranslation();

    return (
        <Modal dialogAs={DraggableModalDialog} className="detail-dialog" show={showDialog} onHide={closeDialog} backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{t('LabDetails.Title') + " - " + quality.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <SampleTable quality={quality} samples={samples} qualityColumns={qualityColumns} />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={closeDialog}>
                    {t("Application.Controls.Close")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default LabDetailsDialog;